import React, { useState, FormEvent } from 'react';
import { InputField } from '../InputField/InputField';

interface FormProps {
  id: string;
  name: string;
  action: string;
  method: 'post' | 'get';
  useAjax?: boolean;
  successMessage?: string;
  errorMessage?: string;
  submitButtonText?: string;
  submitButtonStyle?: string;
  alignment?: string;
  inputBlocks?: any[];
  isOverlay?: boolean;
  zIndex?: number;
  className?: string;
}

export const Form = ({
  id,
  name,
  action,
  method = 'post',
  useAjax = true,
  successMessage = 'Thank you! Your form has been submitted successfully.',
  errorMessage = 'There was an error submitting your form. Please try again.',
  submitButtonText = 'Submit',
  submitButtonStyle = 'primary',
  alignment = 'left',
  inputBlocks = [],
  isOverlay = false,
  zIndex = 10,
  className = '',
}: FormProps) => {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState<string>('');
  
  // Styling classes based on alignment
  const formClasses = {
    left: 'text-left',
    center: 'text-center mx-auto',
    right: 'text-right ml-auto'
  };

  const buttonClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white',
    secondary: 'bg-secondary-600 hover:bg-secondary-700 text-white',
    tertiary: 'bg-gray-600 hover:bg-gray-700 text-white',
    link: 'text-primary-600 hover:text-primary-700 underline bg-transparent'
  };
  
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!useAjax) {
      return true; // Let the form submit normally
    }
    
    setFormState('submitting');
    
    try {
      const form = e.target as HTMLFormElement;
      const formData = new FormData(form);
      
      // Make fetch request to the form action
      const response = await fetch(action, {
        method,
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });
      
      if (response.ok) {
        setFormState('success');
        setFormMessage(successMessage);
        form.reset();
      } else {
        setFormState('error');
        setFormMessage(errorMessage);
      }
    } catch (error) {
      setFormState('error');
      setFormMessage(errorMessage);
    }
  };
  
  const containerStyles: React.CSSProperties = isOverlay
    ? { position: 'absolute', zIndex }
    : {};

  const renderInputBlocks = () => {
    if (!inputBlocks || inputBlocks.length === 0) {
      return null;
    }
    
    return inputBlocks.map((block) => {
      if (block.type !== 'input') {
        return null;
      }
      
      const settings = block.settings;
      
      // Parse options for select/radio/checkbox
      let parsedOptions = [];
      if (settings.input_options && ['select', 'radio', 'checkbox'].includes(settings.input_type)) {
        parsedOptions = settings.input_options.split('\n').map((option: string) => {
          const parts = option.split('|');
          return {
            value: parts[0]?.trim() || '',
            label: parts[1]?.trim() || parts[0]?.trim() || ''
          };
        });
      }
      
      return (
        <div key={block.id} className="mb-4">
          <InputField
            type={settings.input_type || 'text'}
            name={settings.input_name || ''}
            id={settings.input_id || ''}
            label={settings.input_label || ''}
            placeholder={settings.input_placeholder || ''}
            defaultValue={settings.input_value || ''}
            required={settings.input_required === true}
            pattern={settings.input_pattern || ''}
            options={parsedOptions}
          />
        </div>
      );
    });
  };
  
  // Determine message styling based on form state
  const messageClasses = {
    success: 'text-green-600 bg-green-50 border border-green-200 p-4 rounded-md mb-4',
    error: 'text-red-600 bg-red-50 border border-red-200 p-4 rounded-md mb-4',
  };

  return (
    <div
      className={`form-component ${className}`}
      style={containerStyles}
    >
      {formState === 'success' && (
        <div className={messageClasses.success}>
          {formMessage}
        </div>
      )}
      
      {formState === 'error' && (
        <div className={messageClasses.error}>
          {formMessage}
        </div>
      )}
      
      <form
        id={id}
        name={name}
        action={action}
        method={method}
        onSubmit={handleSubmit}
        className={`w-full ${formClasses[alignment as keyof typeof formClasses] || formClasses.left}`}
      >
        <div className="space-y-4">
          {renderInputBlocks()}
        </div>
        
        <div className={`mt-6 ${alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-right' : ''}`}>
          <button
            type="submit"
            disabled={formState === 'submitting'}
            className={`px-6 py-2 rounded-md transition-colors duration-200 ${buttonClasses[submitButtonStyle as keyof typeof buttonClasses] || buttonClasses.primary} ${formState === 'submitting' ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {formState === 'submitting' ? 'Submitting...' : submitButtonText}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Form;